//
// Copyright 2022 DXOS.org
//

import { Event, asyncTimeout } from '@dxos/async';
import { Context } from '@dxos/context';
import { Obj, Query, type QueryResult } from '@dxos/echo';
import { filterMatchDoc } from '@dxos/echo-host/filter';
import { GroupBy, QueryPlanner } from '@dxos/echo-host/query';
import { QueryAST } from '@dxos/echo-protocol';
import { type SpaceId } from '@dxos/keys';
import { log } from '@dxos/log';

import { type ItemsUpdatedEvent, type ObjectCore } from '../core-db';
import { type DatabaseImpl } from '../proxy-db';
import { type QueryContext, type SourceEntry } from './query-context';
import { getTargetSpacesForQuery, isSimpleSelectionQuery, queryHasWindowing } from './util';
import { type WorkingSetDataProvider, type WorkingSetItem, WorkingSetQueryExecutor } from './working-set-executor';

export type GraphQueryContextProps = {
  // TODO(dmaretskyi): Make async.
  onStart: () => void;
  onStop: () => void;
};

/**
 * Query data source.
 * Implemented by a space or a remote agent.
 * Each query has a separate instance.
 */
export interface QuerySource {
  // TODO(dmaretskyi): Update info?
  changed: Event<void>;

  // TODO(dmaretskyi): Make async.
  open(): void;

  // TODO(dmaretskyi): Make async.
  close(): void;

  /**
   * Synchronous query.
   */
  getResults(): SourceEntry[];

  /**
   * Whether this source produces authoritative synchronous results for its current query (via
   * {@link getResults}). Sources that resolve asynchronously (e.g. index- or network-backed) return
   * false so callers can defer the initial subscription event until real results arrive.
   */
  isSynchronous(): boolean;

  /**
   * One-shot query.
   */
  run(ctx: Context, query: QueryAST.Query): Promise<SourceEntry[]>;

  /**
   * Set the filter and trigger continuous updates.
   */
  update(query: QueryAST.Query): void;
}

/**
 * Aggregates multiple query sources.
 */
export class GraphQueryContext implements QueryContext {
  private readonly _sources = new Set<QuerySource>();

  private _query?: QueryAST.Query = undefined;

  private _ctx?: Context = undefined;

  public changed = new Event<void>();

  constructor(private readonly _params: GraphQueryContextProps) {}

  get sources(): ReadonlySet<QuerySource> {
    return this._sources;
  }

  start() {
    this._ctx = new Context();
    this._params.onStart();
    for (const source of this._sources) {
      source.open();
      if (this._query) {
        source.update(this._query);
      }

      // Subscribing after `update` means that we will intentionally skip any `changed` events generated by update.
      source.changed.on(this._ctx, () => {
        this.changed.emit();
      });
    }
  }

  stop() {
    void this._ctx?.dispose();
    for (const source of this.sources) {
      source.close();
    }
    this._params.onStop();
  }

  getResults(): SourceEntry[] {
    // TODO: dedup by meta.key based on scope order (space results take precedence over registry).
    if (!this._query) {
      return [];
    }
    return Array.from(this._sources).flatMap((source) => source.getResults());
  }

  /**
   * Whether any source produces authoritative synchronous results for the current query. False when
   * the query is served only by asynchronous sources (e.g. a feed query served by the index).
   */
  isSynchronous(): boolean {
    if (!this._query) {
      return false;
    }
    return Array.from(this._sources).some((source) => source.isSynchronous());
  }

  async run(
    ctx: Context,
    query: QueryAST.Query,
    { timeout = 30_000 }: QueryResult.RunOptions = {},
  ): Promise<SourceEntry[]> {
    const runTasks = [...this._sources.values()].map(async (s) => {
      try {
        log('run query', {
          resolver: Object.getPrototypeOf(s).constructor.name,
          query: Query.pretty(Query.fromAst(query)),
        });
        const results = await asyncTimeout<SourceEntry[]>(s.run(ctx, query), timeout);
        log('run query results', {
          resolver: Object.getPrototypeOf(s).constructor.name,
          count: results.length,
          query: Query.pretty(Query.fromAst(query)),
        });
        return results;
      } catch (err) {
        log('run query error', {
          resolver: Object.getPrototypeOf(s).constructor.name,
          query: Query.pretty(Query.fromAst(query)),
          error: err,
        });
        throw err;
      }
    });
    if (runTasks.length === 0) {
      return [];
    }
    const mergedResults = (await Promise.all(runTasks)).flatMap((r) => r ?? []);
    return mergedResults;
  }

  update(query: QueryAST.Query): void {
    this._query = query;
    for (const source of this._sources) {
      source.update(query);
    }
  }

  addQuerySource(querySource: QuerySource) {
    this._sources.add(querySource);
    if (this._ctx != null) {
      querySource.changed.on(this._ctx, () => {
        this.changed.emit();
      });
    }
    if (this._query) {
      querySource.update(this._query);
    }
  }
}

/**
 * Queries objects from the local working set.
 */
export class SpaceQuerySource implements QuerySource {
  public readonly changed = new Event<void>();

  private _ctx: Context = new Context();
  private _query: QueryAST.Query | undefined = undefined;
  private _results?: SourceEntry<Obj.Any>[] = undefined;
  private readonly _executor: WorkingSetQueryExecutor;
  private readonly _planner: QueryPlanner;

  constructor(private readonly _database: DatabaseImpl) {
    const provider: WorkingSetDataProvider = {
      get spaceId(): SpaceId {
        return _database.spaceId;
      },
      allCores: () => _database.allObjectCores(),
      getCoreById: (id, load) => _database.getObjectCoreById(id, { load: load ?? false }),
      areStrongDepsSatisfied: (core) => _database.areStrongDepsSatisfied(core),
    };
    this._executor = new WorkingSetQueryExecutor(provider);
    this._planner = new QueryPlanner({ defaultTextSearchKind: 'full-text', noIndexes: true });
  }

  get spaceId() {
    return this._database.spaceId;
  }

  get spaceKey() {
    return this._database.spaceKey;
  }

  open(): void {}

  close() {
    this._results = undefined;
    void this._ctx.dispose().catch(() => {});
  }

  private _onUpdate = (updateEvent?: ItemsUpdatedEvent) => {
    if (!this._query) {
      return;
    }

    // Results haven't been computed yet — no stale cache to invalidate.
    if (!this._results) {
      return;
    }

    if (!updateEvent) {
      this._results = undefined;
      this.changed.emit();
      return;
    }

    // TODO(dmaretskyi): Could be optimized to recompute changed only to the relevant space.
    const changed = updateEvent.itemsUpdated.some(({ id: objectId }) => {
      // If any updated object was in previous results, invalidate.
      if (this._results!.find((result) => result.id === objectId)) {
        return true;
      }

      // For simple queries, use the lightweight filter check for the newly-updated object.
      const trivial = isSimpleSelectionQuery(this._query!);
      if (trivial && !trivial.hasQueues) {
        const core = this._database._entityManager.getObjectCoreById(objectId, { load: false });
        return core != null && this._filterCore(core, trivial.filter, trivial.options);
      }

      // For complex queries (handled by executor), conservatively invalidate on any update.
      // The next getResults() call will recompute via the executor.
      return true;
    });

    if (changed) {
      this._results = undefined;
      this.changed.emit();
    }
  };

  async run(_ctx: Context, query: QueryAST.Query): Promise<SourceEntry<Obj.Unknown>[]> {
    if (!this._isValidSourceForQuery(query) || !this._servesSpaceScope(query) || queryHasWindowing(query)) {
      return [];
    }

    // Actively load by-id targets into the working set before executing. The executor's synchronous
    // getCoreById only kicks off a load, so a one-shot query would otherwise miss objects whose
    // (possibly linked) Automerge document has not been resolved into the working set yet.
    await this._preloadQueryIds(query);

    const items = this._executeWithWorkingSet(query);
    return items === null ? [] : this._mapItemsToResults(items);
  }

  /**
   * Loads any by-id selection targets into the working set. Preserves load-on-demand for
   * `Filter.id(...)` against linked (not-yet-resolved) Automerge documents, which the synchronous
   * executor cannot await. Feed-scoped by-id lookups are served by the index source, not the working set.
   */
  private async _preloadQueryIds(query: QueryAST.Query): Promise<void> {
    const simple = isSimpleSelectionQuery(query);
    if (!simple || simple.hasQueues || simple.filter.type !== 'object' || !simple.filter.id?.length) {
      return;
    }
    await this._database.batchLoadObjectCores([...simple.filter.id]);
  }

  isSynchronous(): boolean {
    // The working set serves space-scoped selections synchronously. Feed-only queries and queries
    // with order/skip/limit clauses contribute nothing here (see `queryHasWindowing`), so they are
    // not synchronous from this source's perspective.
    return this._query !== undefined && this._servesSpaceScope(this._query) && !queryHasWindowing(this._query);
  }

  getResults(): SourceEntry<Obj.Unknown>[] {
    if (!this._query) {
      return [];
    }

    if (!this._results) {
      this._results = this._computeResults(this._query!);
    }

    return this._results!;
  }

  private _computeResults(query: QueryAST.Query): SourceEntry<Obj.Unknown>[] {
    const items = this._executeWithWorkingSet(query);
    return items === null ? [] : this._mapItemsToResults(items);
  }

  update(query: QueryAST.Query): void {
    if (!this._isValidSourceForQuery(query) || !this._servesSpaceScope(query) || queryHasWindowing(query)) {
      this._query = undefined;
      return;
    }

    void this._ctx.dispose().catch(() => {});
    this._ctx = new Context();
    this._query = query;

    this._database._updateEvent.on(this._ctx, this._onUpdate);

    this._results = undefined;
    this.changed.emit();
  }

  /**
   * Executes a query against the in-memory working set. Every query reaching this point (i.e. not
   * already excluded by `run`/`update` for windowing or feed scope) goes through the same planner
   * and executor -- there is no further fast path or query-shape gate. Returns null when the plan
   * requires SQL index capabilities the working set cannot satisfy (full-text or timestamp
   * selectors); the index-backed source handles those. A planner failure is a hard error: the
   * planner must accept any query the source is valid for, so a throw signals a bug rather than an
   * unsupported shape.
   */
  private _executeWithWorkingSet(query: QueryAST.Query): WorkingSetItem[] | null {
    const plan = this._planner.createPlan(query);
    return this._executor.tryExecute(plan);
  }

  /**
   * Maps working-set executor results, computing per-group counts once over the full set
   * (rather than per-item) so grouped queries report accurate counts.
   */
  private _mapItemsToResults(items: WorkingSetItem[]): SourceEntry<Obj.Unknown>[] {
    const groupCounts = new Map<string, number>();
    for (const item of items) {
      if (item.groupKey === undefined) {
        continue;
      }
      const serialized = GroupBy.serializeGroupKey(item.groupKey);
      groupCounts.set(serialized, (groupCounts.get(serialized) ?? 0) + 1);
    }
    return items.map((item) => this._mapItemToResult(item, groupCounts));
  }

  private _mapItemToResult(item: WorkingSetItem, groupCounts?: Map<string, number>): SourceEntry<Obj.Unknown> {
    const serializedGroupKey = item.groupKey !== undefined ? GroupBy.serializeGroupKey(item.groupKey) : undefined;
    return {
      id: item.objectId,
      result: this._database.getObjectById<Obj.Unknown>(item.objectId, { deleted: true }),
      match: { rank: 1 },
      resolution: {
        source: 'local',
        time: 0,
      },
      group:
        item.groupKey !== undefined
          ? { key: item.groupKey, count: groupCounts?.get(serializedGroupKey!) ?? 1 }
          : undefined,
    };
  }

  private _isValidSourceForQuery(query: QueryAST.Query): boolean {
    const targetSpaces = getTargetSpacesForQuery(query);
    // Disabled by spaces filter.
    if (targetSpaces.length > 0 && !targetSpaces.includes(this.spaceId)) {
      return false;
    }

    // Disabled if the from clause has explicit scopes but none target spaces or feeds (e.g. registry-only).
    let hasExplicitNonEmptyScope = false;
    let hasSpaceOrFeedScope = false;
    QueryAST.visit(query, (node) => {
      if (node.type === 'from' && node.from._tag === 'scope' && node.from.scopes.length > 0) {
        hasExplicitNonEmptyScope = true;
        if (node.from.scopes.some((s) => s._tag === 'space' || s._tag === 'feed')) {
          hasSpaceOrFeedScope = true;
        }
      }
    });
    if (hasExplicitNonEmptyScope && !hasSpaceOrFeedScope) {
      return false;
    }

    return true;
  }

  /**
   * Whether the query selects from the space working set: either it has no explicit `from` scope
   * (defaults to the owning space) or its `from` scope includes a space scope. Feed-only queries
   * return false — the working set holds no feed items.
   */
  private _servesSpaceScope(query: QueryAST.Query): boolean {
    let hasExplicitNonEmptyScope = false;
    let hasSpaceScope = false;
    QueryAST.visit(query, (node) => {
      if (node.type === 'from' && node.from._tag === 'scope' && node.from.scopes.length > 0) {
        hasExplicitNonEmptyScope = true;
        if (node.from.scopes.some((s) => s._tag === 'space')) {
          hasSpaceScope = true;
        }
      }
    });
    return !hasExplicitNonEmptyScope || hasSpaceScope;
  }

  private _filterCore(core: ObjectCore, filter: QueryAST.Filter, options: QueryAST.QueryOptions | undefined): boolean {
    return (
      this._database.areStrongDepsSatisfied(core) &&
      filterCoreByDeletedFlag(core, options) &&
      filterMatchDoc(filter, {
        id: core.id,
        doc: core.getObjectStructure(),
        spaceId: this.spaceId,
      })
    );
  }
}

const filterCoreByDeletedFlag = (core: ObjectCore, options: QueryAST.QueryOptions | undefined): boolean => {
  switch (options?.deleted) {
    case undefined:
    case 'exclude':
      return !core.isDeleted();
    case 'include':
      return true;
    case 'only':
      return core.isDeleted();
  }
};
